import { useState } from "react";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@volca/ui/components/ui/toggle-group";
import type { SpreadsheetColumnFilter } from "@/payroll/ai-script/ai-script-spreadsheet-viewer/spreadsheet-region-view";
import { ConditionFilterEditor } from "./condition-filter-editor";
import { ValueFilterEditor } from "./value-filter-editor";

interface SpreadsheetColumnFilterEditorProps {
  values: string[];
  filter: SpreadsheetColumnFilter;
  onChange: (filter: SpreadsheetColumnFilter) => void;
}

const FILTER_MODES = ["values", "condition"] as const;
type FilterMode = (typeof FILTER_MODES)[number];

export const SpreadsheetColumnFilterEditor = ({
  values,
  filter,
  onChange,
}: SpreadsheetColumnFilterEditorProps) => {
  const [mode, setMode] = useState<FilterMode>(
    filter.condition === null ? "values" : "condition",
  );
  const hasValueSelection =
    filter.selectedValues !== null &&
    filter.selectedValues.length !== values.length;
  const hasCondition =
    filter.condition !== null && filter.conditionValue !== "";

  return (
    <Stack
      align="start"
      gap={2}
      className="min-h-0 w-full flex-1 overflow-hidden border-t border-line px-4 py-3"
    >
      <ToggleGroup
        variant="outline"
        size="sm"
        spacing={0}
        aria-label="Filter by"
        value={[mode]}
        onValueChange={(next) => {
          const value = FILTER_MODES.find((option) => option === next[0]);
          if (value) setMode(value);
        }}
        className="w-full"
      >
        {(
          [
            ["values", "Values", hasValueSelection],
            ["condition", "Condition", hasCondition],
          ] as const
        ).map(([tab, label, isSet]) => (
          <ToggleGroupItem key={tab} value={tab} className="flex-1">
            {label}
            {/* Same story as the summary line: `bg-neutral-hovered` is an unwired
                preset near-white, which put this "something is set on this
                tab" dot at 1.03:1 against the tab it sits on. */}
            {isSet && (
              <span className="size-1.5 rounded-full bg-accent-teal-bold" />
            )}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      {mode === "values" ? (
        <ValueFilterEditor
          values={values}
          filter={filter}
          onChange={onChange}
        />
      ) : (
        <ConditionFilterEditor filter={filter} onChange={onChange} />
      )}
    </Stack>
  );
};
